import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import EmployeeSelector from '../../../events/EmployeeSelector'
import WitnessPicker from '../../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
} from '../../../../helpers/medicineWitness'
import { apiError, doseSummary } from './preparedHelpers'

const DISCARD_REASONS = [
  'End of shift',
  'Expired',
  'Not required',
  'Contaminated / dropped',
]

const Summary = ({ prepared }) => (
  <Typography variant="body2" color="text.secondary">
    {prepared.medicineName} · {doseSummary(prepared)}
  </Typography>
)

// Pass a held syringe to a colleague (POST /medsmx/prepared/:id/handover)
export const HandoverDialog = ({ prepared, onClose, onDone }) => {
  const { setToast } = useContext(GlobalContext)
  const [toUser, setToUser] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setToUser(null)
    setError('')
  }, [prepared?._id])

  const submit = () => {
    setBusy(true)
    setError('')
    API.post(`/medsmx/prepared/${prepared._id}/handover`, { toUser: toUser?._id })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: `Handed over to ${res.data?.heldBy?.name ?? 'colleague'}` })
          onDone?.(res.data)
          onClose()
        } else {
          setError(apiError(res, 'Unable to hand this syringe over'))
        }
      })
      .catch(() => setError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open={!!prepared} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>Hand over syringe</DialogTitle>
      <DialogContent>
        {prepared && (
          <Stack gap={2} pt={0.5}>
            <Summary prepared={prepared} />
            <EmployeeSelector
              label="Hand to"
              value={toUser}
              onChange={(e, user) => setToUser(user)}
              preventSelectingSelf
              excludeUserIds={[prepared.heldBy?._id]}
              fullWidth
              size="small"
              required
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!toUser?._id || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Hand over
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Discard the remainder (POST /medsmx/prepared/:id/discard) - controlled
// drugs need a witness
export const DiscardDialog = ({ prepared, onClose, onDone }) => {
  const { setToast } = useContext(GlobalContext)
  const [reason, setReason] = useState('')
  const [witness, setWitness] = useState(emptyWitness())
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setReason(prepared?.isExpired ? 'Expired' : '')
    setWitness(emptyWitness())
    setError('')
  }, [prepared?._id, prepared?.isExpired])

  const isControlled = !!prepared?.isControlled
  const canSubmit =
    !!reason.trim() && (!isControlled || isWitnessComplete(witness)) && !busy

  const submit = () => {
    setBusy(true)
    setError('')
    API.post(`/medsmx/prepared/${prepared._id}/discard`, {
      reason: reason.trim(),
      witness: toWitnessPayload(witness),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: `${prepared.medicineName} discarded` })
          onDone?.(res.data)
          onClose()
        } else {
          setError(apiError(res, 'Unable to discard this syringe'))
        }
      })
      .catch(() => setError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open={!!prepared} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>Discard syringe</DialogTitle>
      <DialogContent>
        {prepared && (
          <Stack gap={2} pt={0.5}>
            <Summary prepared={prepared} />
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {DISCARD_REASONS.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  size="small"
                  color={reason === option ? 'primary' : 'default'}
                  variant={reason === option ? 'filled' : 'outlined'}
                  onClick={() => setReason(option)}
                />
              ))}
            </Stack>
            <TextField
              label="Reason"
              size="small"
              required
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {isControlled && (
              <Stack gap={1.5}>
                <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                  Controlled drug - the discard must be witnessed.
                </Alert>
                <WitnessPicker value={witness} onChange={setWitness} />
              </Stack>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={!canSubmit}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Discard
        </Button>
      </DialogActions>
    </Dialog>
  )
}
